/**
 * Tier 2 classification scoring.
 *
 * Scores proposed theme / topic / sub-topic labels against reference labels.
 * Reports Top-1 and Top-3 accuracy at each level, accuracy per model-confidence
 * band, and a split by subject. Pure functions only: no AI call, no I/O.
 *
 * Labels are compared after normalisation (trimmed, whitespace collapsed,
 * lower-cased). Codes are not compared; see docs/pdf-question-import-poc.md.
 *
 * @format
 */

export type ClassificationSubject = "Business" | "Maths";

export const CLASSIFICATION_LEVELS = ["theme", "topic", "subTopic"] as const;
export type ClassificationLevel = (typeof CLASSIFICATION_LEVELS)[number];

export type LevelLabels = Record<ClassificationLevel, string>;

export interface ClassificationRecord {
	subject: ClassificationSubject;
	/** Reference labels from the seed CSV. */
	reference: LevelLabels;
	/** Ranked guesses per level, best first. */
	predicted: Record<ClassificationLevel, string[]>;
	/** Model confidence per field, each from 0 to 1 inclusive. */
	confidence: Record<ClassificationLevel, number>;
}

export type ConfidenceBandName = "low" | "medium" | "high";

/** Fixed bands. A confidence belongs to a band when min <= confidence < max. */
export const CONFIDENCE_BANDS: readonly { name: ConfidenceBandName; min: number; max: number }[] = [
	{ name: "low", min: 0, max: 0.5 },
	{ name: "medium", min: 0.5, max: 0.8 },
	{ name: "high", min: 0.8, max: Infinity },
];

export interface LevelAccuracy {
	/** Share of records whose reference label is the top guess. Null when there are no records. */
	top1: number | null;
	/** Share of records whose reference label is in the top three guesses. Null when there are no records. */
	top3: number | null;
}

export interface AccuracyReport {
	total: number;
	byLevel: Record<ClassificationLevel, LevelAccuracy>;
}

/**
 * Accuracy for one level within one confidence band. `total` is the number of
 * fields (not questions) whose confidence at this level falls in the band, so
 * it can differ between levels for the same band.
 */
export interface BandLevelAccuracy extends LevelAccuracy {
	total: number;
}

export interface ClassificationScoreReport {
	overall: AccuracyReport;
	bySubject: Record<ClassificationSubject, AccuracyReport>;
	/**
	 * Per-field banding: each field is placed in the band its own confidence
	 * falls in, then scored. This is what sets the per-field auto-accept
	 * threshold. See docs/pdf-question-import-poc.md.
	 */
	byBand: Record<ConfidenceBandName, Record<ClassificationLevel, BandLevelAccuracy>>;
}

/** A numeric code at the start of a label, such as "1", "1.2" or "1.2.1", followed by whitespace. */
const LEADING_CODE_PATTERN = /^\d+(\.\d+)*\.?\s+/;

/**
 * Compare labels by statement text. Strips a leading numeric code, trims,
 * collapses whitespace and lower-cases. Codes are display labels only.
 */
export function normaliseLabel(label: string): string {
	return label.trim().replace(LEADING_CODE_PATTERN, "").replace(/\s+/g, " ").toLowerCase();
}

/** Return the band a confidence falls in. Throws when the confidence is outside 0 to 1. */
export function confidenceBandFor(confidence: number): ConfidenceBandName {
	if (!(confidence >= 0 && confidence <= 1)) {
		throw new RangeError(`Confidence must be between 0 and 1, got ${confidence}`);
	}
	const band = CONFIDENCE_BANDS.find((b) => confidence >= b.min && confidence < b.max);
	// Unreachable: the bands cover 0 to Infinity and the range check above passed.
	if (!band) {
		throw new RangeError(`No confidence band for ${confidence}`);
	}
	return band.name;
}

function isHit(record: ClassificationRecord, level: ClassificationLevel, k: number): boolean {
	const target = normaliseLabel(record.reference[level]);
	return record.predicted[level].slice(0, k).some((guess) => normaliseLabel(guess) === target);
}

function accuracy(records: ClassificationRecord[], level: ClassificationLevel, k: number): number | null {
	if (records.length === 0) {
		return null;
	}
	const hits = records.filter((record) => isHit(record, level, k)).length;
	return hits / records.length;
}

function summarise(records: ClassificationRecord[]): AccuracyReport {
	const byLevel = {} as Record<ClassificationLevel, LevelAccuracy>;
	for (const level of CLASSIFICATION_LEVELS) {
		byLevel[level] = { top1: accuracy(records, level, 1), top3: accuracy(records, level, 3) };
	}
	return { total: records.length, byLevel };
}

/**
 * Accuracy for one level over a specific set of records (those whose confidence
 * at this level falls in a given band). `total` counts those records' fields.
 */
function summariseLevel(records: ClassificationRecord[], level: ClassificationLevel): BandLevelAccuracy {
	return {
		total: records.length,
		top1: accuracy(records, level, 1),
		top3: accuracy(records, level, 3),
	};
}

function formatPercent(value: number | null): string {
	return value === null ? "n/a" : `${(value * 100).toFixed(1)}%`;
}

function formatBlock(title: string, report: AccuracyReport): string[] {
	const lines = [`${title} (${report.total} questions)`];
	for (const level of CLASSIFICATION_LEVELS) {
		const { top1, top3 } = report.byLevel[level];
		lines.push(
			`  ${level.padEnd(9)} top-1 ${formatPercent(top1).padStart(7)}   top-3 ${formatPercent(top3).padStart(7)}`,
		);
	}
	return lines;
}

/** Render a confidence band, where each level has its own field count. */
function formatBandBlock(name: ConfidenceBandName, byLevel: Record<ClassificationLevel, BandLevelAccuracy>): string[] {
	const lines = [`Confidence ${name}`];
	for (const level of CLASSIFICATION_LEVELS) {
		const { total, top1, top3 } = byLevel[level];
		lines.push(
			`  ${level.padEnd(9)} ${`${total} fields`.padStart(10)}   ` +
				`top-1 ${formatPercent(top1).padStart(7)}   top-3 ${formatPercent(top3).padStart(7)}`,
		);
	}
	return lines;
}

/**
 * Format a score report as plain text. `source` says where the predictions came
 * from, so a report built on fixtures cannot be mistaken for real accuracy.
 */
export function formatClassificationReport(report: ClassificationScoreReport, source: string): string {
	const lines = ["Tier 2 classification accuracy", `Source: ${source}`, ""];
	lines.push(...formatBlock("Overall", report.overall), "");
	for (const subject of ["Business", "Maths"] as const) {
		lines.push(...formatBlock(subject, report.bySubject[subject]), "");
	}
	for (const band of CONFIDENCE_BANDS) {
		lines.push(...formatBandBlock(band.name, report.byBand[band.name]), "");
	}
	return lines.join("\n").trimEnd();
}

/** Score a set of records overall, by subject and by per-field confidence band. */
export function scoreClassification(records: ClassificationRecord[]): ClassificationScoreReport {
	// Validate every field's confidence up front, so a bad value fails fast
	// rather than silently landing (or not) in a band.
	for (const record of records) {
		for (const level of CLASSIFICATION_LEVELS) {
			confidenceBandFor(record.confidence[level]);
		}
	}

	const bySubject = {} as Record<ClassificationSubject, AccuracyReport>;
	for (const subject of ["Business", "Maths"] as const) {
		bySubject[subject] = summarise(records.filter((record) => record.subject === subject));
	}

	const byBand = {} as Record<ConfidenceBandName, Record<ClassificationLevel, BandLevelAccuracy>>;
	for (const band of CONFIDENCE_BANDS) {
		const byLevel = {} as Record<ClassificationLevel, BandLevelAccuracy>;
		for (const level of CLASSIFICATION_LEVELS) {
			// A field belongs to the band its own confidence at this level falls in.
			const inBand = records.filter((record) => confidenceBandFor(record.confidence[level]) === band.name);
			byLevel[level] = summariseLevel(inBand, level);
		}
		byBand[band.name] = byLevel;
	}

	return { overall: summarise(records), bySubject, byBand };
}
